import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import IFlightRangeData from '../types/FlightRanges';
import FlightRangeForm from '../Components/FlightRange/FlightRangeForm';
import FlightRangeModifyTable from '../Components/FlightRange/FlightRangeModifyTable';
import FlightRangeFooterActions from '../Components/FlightRange/FlightRangeFooterActions';
import { useFlightTypeDescriptions } from '../hooks/useFlightTypeDescriptions';
import './FlightRangeModify.scss';
import './FlightRangeModify.loading.scss';
import '../Components/FlightRange/FlightTypeDropdown.scss';

interface LocationState {
  selectedFlightRanges: IFlightRangeData[];
  selectedIds: string[];
}

export interface ReplacementRange {
  id: string;
  marketingCarrier: string;
  start: number;
  end: number;
  flightType: string;
  flightTypeDescription: string;
  effectiveDate: string;
  operatingCarrier: string;
  discontinueDate?: string;
}

interface FormSnapshot {
  flightRangeStart: string;
  flightRangeEnd: string;
  operatingCarrier: string;
  flightType: string;
  effectiveDate: string;
  wetLease: string;
}

/**
 * Main component for modifying flight ranges
 */
const FlightRangeModify: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  // Form state
  const [flightRangeStart, setFlightRangeStart] = useState<string>('');
  const [flightRangeEnd, setFlightRangeEnd] = useState<string>('');
  const [operatingCarrier, setOperatingCarrier] = useState<string>('');
  const [flightType, setFlightType] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [wetLease, setWetLease] = useState<string>('no');

  // Data state
  const [replacementRanges, setReplacementRanges] = useState<ReplacementRange[]>([]);
  const [currentSelectedRanges, setCurrentSelectedRanges] = useState<IFlightRangeData[]>([]);
  const { flightTypes, flightTypeOptions, isLoading } = useFlightTypeDescriptions();

  // Reference to track form changes
  const lastSubmittedValues = useRef<FormSnapshot | null>(null);

  // Initialize form data from selected ranges
  useEffect(() => {
    if (state?.selectedFlightRanges) {
      setCurrentSelectedRanges([...state.selectedFlightRanges]);
    }
  }, [state]);

  // Find matching flight type and initialize form when data is loaded
  useEffect(() => {
    if (currentSelectedRanges.length > 0 && flightTypes.length > 0) {
      const firstRange = currentSelectedRanges[0];

      // Find the matching flight type from the API data
      // Try to find an exact match first
      let matchingFlightType = flightTypes.find(
        description => description === firstRange.flightTypeDescription
      );

      // If no exact match, try a case-insensitive match
      if (!matchingFlightType) {
        matchingFlightType = flightTypes.find(
          description => description.toLowerCase() ===
            (firstRange.flightTypeDescription ? firstRange.flightTypeDescription.toLowerCase() : '')
        );
      }

      // If still no match, try a partial match
      if (!matchingFlightType && firstRange.flightTypeDescription) {
        const lowerDescription = firstRange.flightTypeDescription.toLowerCase();
        matchingFlightType = flightTypes.find(
          description => description.toLowerCase().includes(lowerDescription) ||
                         lowerDescription.includes(description.toLowerCase())
        );
      }

      setFlightRangeStart(String(firstRange.start || ''));
      setFlightRangeEnd(String(firstRange.end || ''));

      if (firstRange.operatingCarrier) {
        setOperatingCarrier(firstRange.operatingCarrier);
      }

      if (matchingFlightType) {
        setFlightType(matchingFlightType);
      }

      setEffectiveDate(firstRange.effectiveDate || '');
    }
  }, [currentSelectedRanges, flightTypes]);

  // Redirect to flight ranges list if no ranges are selected
  useEffect(() => {
    if (!state || !state.selectedFlightRanges || state.selectedFlightRanges.length === 0) {
      navigate('/flight-ranges', { replace: true });
    }
  }, [state, navigate]);

  // Early return if no state
  if (!state || !state.selectedFlightRanges) {
    return null;
  }

  // Handler functions
  const handleCancel = () => {
    navigate('/flight-ranges');
  };

  const handleRemoveRange = (rangeId: string) => {
    setCurrentSelectedRanges(prevRanges =>
      prevRanges.filter(range => range.id !== rangeId)
    );
  };

  const hasFormChanged = (currentValues: FormSnapshot, lastValues: FormSnapshot | null): boolean => {
    if (!lastValues) return true;

    return (
      currentValues.flightRangeStart !== lastValues.flightRangeStart ||
      currentValues.flightRangeEnd !== lastValues.flightRangeEnd ||
      currentValues.operatingCarrier !== lastValues.operatingCarrier ||
      currentValues.flightType !== lastValues.flightType ||
      currentValues.effectiveDate !== lastValues.effectiveDate ||
      currentValues.wetLease !== lastValues.wetLease
    );
  };

  const handleUpdate = () => {
    // Validate required fields before adding replacement row
    if (!flightType || !effectiveDate) {
      alert('Please fill in all required fields (Flight Type and Effective Date) before updating.');
      return;
    }

    const currentFormValues: FormSnapshot = {
      flightRangeStart,
      flightRangeEnd,
      operatingCarrier,
      flightType,
      effectiveDate,
      wetLease
    };

    // Check if form values have changed since last submission
    if (!hasFormChanged(currentFormValues, lastSubmittedValues.current)) {
      alert('No changes detected. Please modify at least one field before updating.');
      return;
    }

    // Use the flightType directly as it now contains the description
    const selectedFlightTypeDescription = flightType;

    // Create a new replacement range with current form values
    const start = parseInt(flightRangeStart) || Math.min(...currentSelectedRanges.map(r => r.start));
    const end = parseInt(flightRangeEnd) || Math.max(...currentSelectedRanges.map(r => r.end));

    console.log('Creating replacement range with values:', {
      operatingCarrier,
      currentSelectedRange: currentSelectedRanges[0]
    });

    const newReplacementRange: ReplacementRange = {
      id: `replacement-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      marketingCarrier: currentSelectedRanges[0].marketingCarrier,
      start: start,
      end: end,
      flightType: flightType,
      flightTypeDescription: selectedFlightTypeDescription,
      effectiveDate: effectiveDate,
      operatingCarrier: operatingCarrier || currentSelectedRanges[0].operatingCarrier,
      discontinueDate: currentSelectedRanges[0].discontinueDate
    };

    console.log('Created replacement range:', newReplacementRange);

    // Add the new replacement range to the existing list
    setReplacementRanges(prevRanges => [...prevRanges, newReplacementRange]);

    // Update the last submitted values
    lastSubmittedValues.current = { ...currentFormValues };
  };

  const handleReset = () => {
    setFlightRangeStart('');
    setFlightRangeEnd('');
    setOperatingCarrier('');
    setFlightType('');
    setEffectiveDate('');
    setWetLease('no');
    setReplacementRanges([]); // Clear all replacement ranges on reset
    lastSubmittedValues.current = null; // Reset the last submitted values
    // Reset selected ranges to original state
    if (state?.selectedFlightRanges) {
      setCurrentSelectedRanges([...state.selectedFlightRanges]);
    }
  };

  const handleSaveDraft = () => {
    // Implement save as draft functionality
    console.log('Save as draft functionality to be implemented');
  };

  const handleModify = () => {
    // Implement modify functionality
    console.log('Modify flight ranges functionality to be implemented');
  };

  // Define options for dropdowns
  const carrierOptions = [
    { value: 'AS', label: 'AS' },
    { value: 'QX', label: 'QX' },
    { value: 'OO', label: 'OO' },
    { value: 'XX', label: 'XX' },
  ];

  return (
    <div className="flight-range-modify">
      <div className="flight-range-modify__header">
        <h1 className="flight-range-modify__title">Modify flight ranges.</h1>
      </div>

      <div className="flight-range-modify__content">
        {/* Form component */}
        <FlightRangeForm
          values={{
            flightRangeStart,
            flightRangeEnd,
            operatingCarrier,
            flightType,
            effectiveDate,
            wetLease
          }}
          onChange={{
            setFlightRangeStart,
            setFlightRangeEnd,
            setOperatingCarrier,
            setFlightType,
            setEffectiveDate,
            setWetLease
          }}
          flightTypeOptions={flightTypeOptions}
          carrierOptions={carrierOptions}
          isLoadingFlightTypes={isLoading}
          onUpdate={handleUpdate}
          onReset={handleReset}
        />

        <div className="flight-range-modify__tables-container">
          {/* Current selected ranges table */}
          <FlightRangeModifyTable
            title="Selected Flight Ranges"
            ranges={currentSelectedRanges}
            showRemoveButton={true}
            onRemove={handleRemoveRange}
          />

          {/* Replacement ranges table, only shown when there are replacement ranges */}
          {replacementRanges.length > 0 && (
            <FlightRangeModifyTable
              title="Replacement flight(s)"
              ranges={replacementRanges}
            />
          )}
        </div>
      </div>

      {/* Footer actions */}
      <FlightRangeFooterActions
        onCancel={handleCancel}
        onSaveDraft={handleSaveDraft}
        onModify={handleModify}
      />
    </div>
  );
};

export default FlightRangeModify;